<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNumber } from '@composables/number';
import type { ReservePack } from '@store/api/interfaces';

/**
 * Карточка пакета гигабайт на странице оплаты: объём и цена в одну строку.
 * Повторяет геометрию карточки тарифа (радиус, рамка выбора): вкладки
 * переключают содержимое на месте, и разная пластика читалась бы как переход
 * на другую страницу. Скидок у пакетов не бывает: ни бейджа, ни зачёркнутой цены.
 */
const { t } = useI18n();
const { withSpaces } = useNumber();

const props = defineProps<{
	pack: ReservePack;
	selected: boolean;
}>();

defineEmits<{
	(e: 'select'): void;
}>();

const sizeFormatted = computed((): string => {
	return t('reserve.gb', { n: props.pack.gb });
});

const priceFormatted = computed((): string => {
	return `${withSpaces(props.pack.cost.amount)} ${t(`common.currency.${props.pack.cost.currency}`)}`;
});
</script>

<template>
	<div
		class="pack-card"
		:class="{ 'pack-card_selected': selected }"
		@click="$emit('select')"
	>
		<div class="pack-card__inner">
			<div class="pack-card__row">
				<span class="pack-card__size">{{ sizeFormatted }}</span>
				<span class="pack-card__price">{{ priceFormatted }}</span>
			</div>
		</div>
	</div>
</template>

<style lang="scss">
$pack-bg-light: #fff;
$pack-bg-light-selected: #faf8ff;

.pack-card {
	position: relative;
	border-radius: 2.4rem;
	cursor: pointer;

	// ::before — обычная рамка, ::after — градиентная у выбранной карточки.
	// Обе лежат всегда и меняются прозрачностью, иначе на переключении
	// вкладок видно, как рамка перерисовывается.
	&::before,
	&::after {
		content: '';
		position: absolute;
		inset: -1px;
		border-radius: 2.4rem;
		z-index: 0;
		pointer-events: none;
		transition: opacity 0.3s ease;
	}

	&::before {
		background: rgba(0, 28, 51, 0.12);
		opacity: 1;

		@include theme-dark {
			background: transparent;
			border: 1px solid rgba(255, 255, 255, 0.12);
		}
	}

	&::after {
		background: linear-gradient(196deg, rgba(200, 45, 252, 0.45) 0%, #792DFC 25%, #C82DFC 62%, rgba(121, 45, 252, 0.45) 100%);
		opacity: 0;

		@include theme-dark {
			background: linear-gradient(196deg, rgba(255, 255, 255) 0%, rgb(175, 129, 253) 25%, rgb(222, 129, 253) 62%, rgba(255, 255, 255) 100%);
			padding: 1px;
			-webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
			-webkit-mask-composite: xor;
			mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
			mask-composite: exclude;
		}
	}

	&_selected {
		&::before { opacity: 0; }
		&::after { opacity: 1; }

		.pack-card__inner {
			background-color: $pack-bg-light-selected;

			@include theme-dark {
				background-color: rgba(121, 45, 252, 0.16);
			}
		}
	}

	&_selected &__price {
		color: #792DFC;

		@include theme-dark {
			color: #BC96FD;
		}
	}

	&__inner {
		position: relative;
		z-index: 2;
		padding: 1.2rem;
		background-color: $pack-bg-light;
		border-radius: calc(2.4rem - 1px);
		transition: background-color 0.3s ease;

		@include theme-dark {
			background-color: transparent;
		}

		@include min-desktop {
			padding: 2rem 2.4rem;
		}
	}

	&__row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 0.8rem;
		min-height: 3rem;
	}

	&__size,
	&__price {
		@include font-16-pro-medium;

		@include min-desktop {
			font-size: 2.1rem;
			line-height: 1;
		}
	}

	&__price {
		font-weight: bold;
		color: var(--color-text);
		transition: color 0.3s ease;
	}
}
</style>
